import React from 'react';
import { cn } from '../../../lib/utils';
import { acctFieldLabel } from '../shared/typography';
import { useTheme } from '../../../contexts/ThemeContext';

export const PaymentField: React.FC<{ label: string; children: React.ReactNode; className?: string }> = ({
  label,
  children,
  className,
}) => {
  const { colors } = useTheme();
  return (
    <div className={className}>
      <p className={cn(acctFieldLabel, 'mb-1', colors.textMuted)}>{label}</p>
      {children}
    </div>
  );
};

export const PaymentPanelTitle: React.FC<{ children: React.ReactNode; accent?: 'indigo' | 'slate' }> = ({
  children,
  accent = 'indigo',
}) => {
  const { colors } = useTheme();
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'h-2 w-2 rounded-full shrink-0',
          accent === 'indigo' && 'bg-indigo-500',
          accent === 'slate' && 'bg-slate-400',
        )}
      />
      <h2 className={cn('text-sm font-semibold', colors.text)}>{children}</h2>
    </div>
  );
};

export const PaymentAmountBlock: React.FC<{
  currencyCode: string;
  bankAmountLocal: React.ReactNode;
  bankAmountUsd: React.ReactNode;
  onAllocate: () => void;
  allocateDisabled: boolean;
  children: React.ReactNode;
}> = ({ currencyCode, bankAmountLocal, bankAmountUsd, onAllocate, allocateDisabled, children }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('rounded-lg border px-2.5 py-2 space-y-1.5', colors.info, colors.border)}>
      <div className="grid grid-cols-2 gap-1.5">{children}</div>
      <div>
        <p className={cn(acctFieldLabel, 'mb-1', colors.primaryFg)}>Bank outflow</p>
        <div className="flex items-center gap-1.5">
          <div className="flex-1 min-w-0">{bankAmountLocal}</div>
          {currencyCode && (
            <span className={cn('text-xs font-semibold shrink-0', colors.primaryFg)}>
              {currencyCode}
            </span>
          )}
        </div>
      </div>
      <div className={cn('flex items-center justify-between gap-2 pt-1 border-t', colors.border)}>
        <div className={cn('text-[11px]', colors.textMuted)}>
          USD <span className={cn('font-semibold tabular-nums', colors.textSecondary)}>{bankAmountUsd || '—'}</span>
        </div>
        <button
          type="button"
          disabled={allocateDisabled}
          onClick={onAllocate}
          className={cn(
            'text-[11px] font-medium rounded-md px-2 py-1 transition-colors border',
            allocateDisabled
              ? cn(colors.textMuted, 'cursor-not-allowed opacity-60', colors.border)
              : cn(colors.primaryFg, colors.surface, colors.primaryBorderAll, colors.interactiveHover),
          )}
        >
          Reallocate (oldest first)
        </button>
      </div>
    </div>
  );
};

export const PaymentStatusRow: React.FC<{
  canSave: boolean;
  amountMatch: boolean;
  balanced: boolean;
  hasOverPay: boolean;
  partialCount: number;
  fullCount: number;
}> = ({ canSave, amountMatch, balanced, hasOverPay, partialCount, fullCount }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
          canSave ? colors.success : hasOverPay ? colors.error : colors.warning,
        )}
      >
        <span className={cn('h-1.5 w-1.5 rounded-full', canSave ? 'bg-emerald-500' : hasOverPay ? 'bg-red-500' : 'bg-amber-500')} />
        {canSave ? 'Ready to save' : hasOverPay ? 'Over limit on a line' : !balanced ? 'Out of balance' : !amountMatch ? 'Bank ≠ pay total' : 'Add invoices'}
      </span>
      {(partialCount > 0 || fullCount > 0) && (
        <span className={cn('text-xs', colors.textMuted)}>
          {fullCount > 0 && (
            <span className={isDark ? 'text-emerald-400' : 'text-emerald-600'}>{fullCount} full</span>
          )}
          {fullCount > 0 && partialCount > 0 && ' · '}
          {partialCount > 0 && (
            <span className={isDark ? 'text-amber-400' : 'text-amber-600'}>{partialCount} partial</span>
          )}
        </span>
      )}
    </div>
  );
};

export const PaymentInvoicePanelHeader: React.FC<{
  apSelect: React.ReactNode;
  selectButton: React.ReactNode;
  fillOldestButton?: React.ReactNode;
  invoiceCount: number;
}> = ({ apSelect, selectButton, fillOldestButton, invoiceCount }) => {
  const { colors } = useTheme();
  return (
    <div className={cn('px-4 py-3 border-b', colors.border, colors.headerBg)}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <PaymentPanelTitle>Invoices to pay</PaymentPanelTitle>
          <p className={cn('text-xs mt-1 pl-4', colors.textMuted)}>
            {invoiceCount > 0
              ? `${invoiceCount} line${invoiceCount === 1 ? '' : 's'} · matched by link #`
              : 'Select open payables from AP account'}
          </p>
        </div>
        <div className="flex flex-wrap items-stretch gap-2 sm:shrink-0 sm:justify-end">
          <div className="min-w-[14rem] flex-1 sm:flex-none sm:min-w-[24rem] sm:w-[28rem]">{apSelect}</div>
          {fillOldestButton}
          {selectButton}
        </div>
      </div>
    </div>
  );
};

export const PaymentEmptyInvoices: React.FC = () => {
  const { colors } = useTheme();
  return (
    <div className={cn('m-4 rounded-lg border-2 border-dashed', colors.border, colors.headerBg)}>
      <div className="flex flex-col items-center justify-center py-10 px-6 text-center">
        <span className={cn('inline-flex h-11 w-11 items-center justify-center rounded-xl mb-3', colors.info)}>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.121a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
          </svg>
        </span>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>No invoices yet</p>
        <p className={cn('text-xs mt-1 max-w-[18rem]', colors.textMuted)}>
          Enter bank outflow, then pay oldest open items until the amount is covered (last may be partial).
        </p>
      </div>
    </div>
  );
};

export const PaymentReconcileStrip: React.FC<{
  payTotal: number;
  bankTotal: number;
  usdTotal: number;
  matched: boolean;
  invoiceCount: number;
}> = ({ payTotal, bankTotal, usdTotal, matched, invoiceCount }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div
      className={cn(
        'px-4 py-2.5 border-t flex flex-wrap items-center justify-between gap-3 text-sm',
        matched ? colors.success : colors.warning,
      )}
    >
      <span className={cn('text-xs', colors.textMuted)}>
        Settling <strong className={colors.textSecondary}>{invoiceCount}</strong> invoice{invoiceCount === 1 ? '' : 's'}
      </span>
      <div className="flex items-center gap-4 tabular-nums">
        <div className="text-right">
          <p className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>Pay total</p>
          <p className={cn('font-bold', isDark ? 'text-amber-400' : 'text-amber-700')}>{payTotal.toFixed(2)}</p>
        </div>
        <span className={cn('text-xl font-light leading-none', matched ? 'text-emerald-500' : 'text-amber-500')}>
          {matched ? '=' : '≠'}
        </span>
        <div className="text-right">
          <p className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>Bank</p>
          <p className={cn('font-bold', matched ? colors.text : (isDark ? 'text-amber-400' : 'text-amber-700'))}>
            {bankTotal.toFixed(2)}
          </p>
        </div>
        <div className={cn('hidden sm:block text-right pl-2 border-l', colors.border)}>
          <p className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>Cr USD</p>
          <p className={cn('font-semibold', colors.textMuted)}>{usdTotal.toFixed(2)}</p>
        </div>
      </div>
    </div>
  );
};
